import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

import Storage from '../utils/storage';

const GRANT_KEY = 'vida_biometric_grant';
const ENROLLED_FLAG_KEY = 'vida_biometric_enrolled';
const USER_EMAIL_KEY = 'vida_biometric_user_email';
const DECLINED_KEY = 'vida_biometric_declined';

const isNative = Platform.OS !== 'web';

// Grant is NOT keychain-biometric-gated — the biometric prompt is enforced
// in JS via LocalAuthentication.authenticateAsync BEFORE we touch this slot.
// Combining OS-level + JS gates double-prompts Face ID on every sign-in.
// WHEN_UNLOCKED_THIS_DEVICE_ONLY still blocks iCloud Keychain backup.
const GRANT_OPTIONS: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
};

export const storeBiometricGrant = async (grant: string): Promise<void> => {
  if (!isNative) return;
  await SecureStore.setItemAsync(GRANT_KEY, grant, GRANT_OPTIONS);
};

export const readBiometricGrant = async (): Promise<string | null> => {
  if (!isNative) return null;
  try {
    return await SecureStore.getItemAsync(GRANT_KEY, GRANT_OPTIONS);
  } catch {
    return null;
  }
};

export const clearBiometricGrant = async (): Promise<void> => {
  if (!isNative) return;
  try {
    await SecureStore.deleteItemAsync(GRANT_KEY);
  } catch {
    /* key may already be absent */
  }
};

// Written by setBiometricEnrolled (from enrollBiometric) and cleared by
// clearAllBiometricState. Callers check this on the sign-in screen to know
// whether to auto-trigger biometric — separate from the grant slot itself.
export const isBiometricEnrolled = async (): Promise<boolean> => {
  return (await Storage.getItem(ENROLLED_FLAG_KEY)) === 'true';
};

export const setBiometricEnrolled = async (enrolled: boolean): Promise<void> => {
  if (enrolled) await Storage.setItem(ENROLLED_FLAG_KEY, 'true');
  else await Storage.removeItem(ENROLLED_FLAG_KEY);
};

export const storeBiometricUserEmail = async (email: string): Promise<void> => {
  await Storage.setItem(USER_EMAIL_KEY, email);
};

export const readBiometricUserEmail = async (): Promise<string | null> => {
  return Storage.getItem(USER_EMAIL_KEY);
};

// "Not now" preference — persists across sessions so we don't re-prompt.
// Cleared on Settings toggle-on; preserved across a revoke.
export const markBiometricDeclined = async (): Promise<void> => {
  await Storage.setItem(DECLINED_KEY, 'true');
};

export const wasBiometricDeclined = async (): Promise<boolean> => {
  return (await Storage.getItem(DECLINED_KEY)) === 'true';
};

export const clearBiometricDeclined = async (): Promise<void> => {
  await Storage.removeItem(DECLINED_KEY);
};

// Wipe on /biometric/exchange 401 or Settings toggle-off. Preserves the
// declined flag so re-enrolling requires explicit user action.
export const clearAllBiometricState = async (): Promise<void> => {
  await clearBiometricGrant();
  await Storage.removeItem(ENROLLED_FLAG_KEY);
  await Storage.removeItem(USER_EMAIL_KEY);
};
